import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { DateTime } from 'luxon';

import Layout from '../../layout/Layout';
import Card from '../../components/Card/Card';
import Table from '../../components/Table/Table';
import TableHead from '../../components/Table/TableHead';
import TableHeadData from '../../components/Table/TableHeadData';
import TableRow from '../../components/Table/TableRow';
import TableData from '../../components/Table/TableData';
import TableBody from '../../components/Table/TableBody';
import EmptyTable from '../../components/Table/EmptyTable';
import Badge from '../../components/Badge/Badge';
import Button from '../../components/Button/Button';
import ButtonTypes from '../../constants/buttonTypes';
import Pagination from '../../components/Pagination/Pagination';
import PermissionsGate from '../../components/PermissionsGate/PermissionsGate';
import AccessDenied from '../../components/AccessDenied/AccessDenied';
import SearchInput from '../../components/SearchInput/SearchInput';
import CustomSelect from '../../components/Select/CustomSelect';
import Modal from '../../components/Modal/Modal';
import Alert from '../../components/Alert/Alert';
import AlertType from '../../common/alertType';
import { permissions } from '../../common/permissionGroup';
import { IconCode } from '../../assets/icon';
import getSupplierApiLogs from '../../services/supplierApiLogs';

const actionOptions = [
  { value: '', label: 'All actions' },
  { value: 'authentication', label: 'Authentication' },
  { value: 'availability', label: 'Availability' },
  { value: 'pricing', label: 'Pricing' },
  { value: 'quote_availability', label: 'Quote availability' },
  { value: 'quote_pricing', label: 'Quote pricing' },
  { value: 'quote', label: 'Quote' },
  { value: 'order', label: 'Order' },
  { value: 'tracking', label: 'Tracking' },
  { value: 'google_sheet_append', label: 'Google Sheets append' },
];

const statusOptions = [
  { value: '', label: 'All results' },
  { value: '1', label: 'Success' },
  { value: '0', label: 'Failed' },
];

const SupplierApiLogs = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [query, setQuery] = useState('');
  const [action, setAction] = useState(actionOptions[0]);
  const [status, setStatus] = useState(statusOptions[0]);
  const [selectedLog, setSelectedLog] = useState();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();

  const loadLogs = (page = currentPage) => {
    setLoading(true);
    setError(undefined);
    getSupplierApiLogs({
      page,
      query: query || undefined,
      action: action.value || undefined,
      success: status.value === '' ? undefined : status.value,
    })
      .then((result) => {
        setLogs(result.data || []);
        setCurrentPage(result.current_page || 1);
        setLastPage(result.last_page || 1);
      })
      .catch((requestError) => setError(requestError?.message || 'Could not load supplier API logs.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    const timer = setTimeout(() => loadLogs(1), 500);
    return () => clearTimeout(timer);
  }, [query, action, status]);

  const breadcrumbs = {
    pageTitle: 'Supplier API Logs',
    history: [
      { title: 'Home', onClick: () => navigate('/') },
      { title: 'Supplier API Logs' },
    ],
  };

  return (
    <Layout breadcrumbs={breadcrumbs}>
      <PermissionsGate can={permissions.LOGS_READ} errorComponent={<AccessDenied />}>
        <Card
          showHeader
          headerLeft={(
            <div className="w-full sm:w-[280px]">
              <SearchInput
                isClearable
                value={query}
                onChange={({ target: { value } }) => setQuery(value)}
              />
            </div>
          )}
          headerRight={(
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-[10px] w-full sm:w-[430px]">
              <CustomSelect options={actionOptions} value={action} onChange={setAction} />
              <CustomSelect options={statusOptions} value={status} onChange={setStatus} />
            </div>
          )}
          headerBordered={false}
          footerBordered={false}
          footer={(
            <div className="flex justify-end">
              <Pagination
                currentPage={currentPage}
                lastPage={lastPage}
                onPagePress={(page) => loadLogs(page)}
              />
            </div>
          )}
        >
          {error && <Alert text={error} type={AlertType.warning} />}
          <Table
            loading={loading}
            mobileVisibleFields={['Time', 'Supplier', 'Action', 'Result']}
          >
            <TableHead>
              <TableRow bordered>
                <TableHeadData>Time</TableHeadData>
                <TableHeadData>Supplier</TableHeadData>
                <TableHeadData>User</TableHeadData>
                <TableHeadData>Action</TableHeadData>
                <TableHeadData>Endpoint</TableHeadData>
                <TableHeadData>Result</TableHeadData>
                <TableHeadData>Reference</TableHeadData>
                <TableHeadData />
              </TableRow>
            </TableHead>
            <TableBody>
              {logs.length ? logs.map((log, index) => (
                <TableRow key={log.id} bordered={index < logs.length - 1}>
                  <TableData>
                    {DateTime.fromISO(log.created_at, { zone: 'utc' }).toLocal()
                      .toFormat('yyyy-MM-dd HH:mm:ss')}
                  </TableData>
                  <TableData><Badge text={log.supplier} type={ButtonTypes.LIGHT_PRIMARY} /></TableData>
                  <TableData>{log.user?.email || 'System'}</TableData>
                  <TableData>{log.action}</TableData>
                  <TableData>
                    <span className="break-all">{`${log.method} ${log.endpoint}`}</span>
                  </TableData>
                  <TableData>
                    <Badge
                      text={log.success ? `${log.status_code || 'OK'}` : `${log.status_code || 'Error'}`}
                      type={log.success ? ButtonTypes.LIGHT_SUCCESS : ButtonTypes.LIGHT_DANGER}
                    />
                  </TableData>
                  <TableData>{log.external_reference || '—'}</TableData>
                  <TableData>
                    <Button
                      containerStyle="p-[7px]"
                      type={ButtonTypes.LIGHT}
                      icon={<IconCode className="w-[16px]" />}
                      onClick={() => setSelectedLog(log)}
                    />
                  </TableData>
                </TableRow>
              )) : <EmptyTable colSpan={8} />}
            </TableBody>
          </Table>
        </Card>

        <Modal
          visible={Boolean(selectedLog)}
          toggleVisible={() => setSelectedLog(undefined)}
          title="Supplier API log"
          subTitle={selectedLog
            ? `${selectedLog.supplier} · ${selectedLog.method} ${selectedLog.endpoint}`
            : undefined}
          hideSaveBtn
        >
          <div className="w-[900px] max-w-full max-h-[70vh] overflow-auto customScroll">
            {selectedLog?.error_message && (
              <Alert text={selectedLog.error_message} type={AlertType.warning} />
            )}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-[14px] mt-[14px]">
              <div>
                <div className="font-semibold mb-[6px]">Request</div>
                <pre
                  className={`text-[12px] whitespace-pre-wrap break-all bg-input-bg
                    dark:bg-[#1b263b] p-[12px] rounded-[6px]`}
                >
                  {JSON.stringify(selectedLog?.request_payload || {}, null, 2)}
                </pre>
              </div>
              <div>
                <div className="font-semibold mb-[6px]">Response</div>
                <pre
                  className={`text-[12px] whitespace-pre-wrap break-all bg-input-bg
                    dark:bg-[#1b263b] p-[12px] rounded-[6px]`}
                >
                  {JSON.stringify(selectedLog?.response_payload || {}, null, 2)}
                </pre>
              </div>
            </div>
          </div>
        </Modal>
      </PermissionsGate>
    </Layout>
  );
};

export default SupplierApiLogs;
